import React, { useState } from 'react';
import { Meeting, AgendaItem } from '../../../../types/meeting';

interface MeetingAgendaPanelProps {
  meeting: Meeting;
  isGenerating: boolean;
  onGenerate: (focus?: string) => void;
  /** Secretary/Admin only: enables manual editing of the agenda items. */
  canEdit?: boolean;
  /** Persist a manually edited agenda (full replace via PUT). */
  onSave?: (items: AgendaItem[]) => Promise<void>;
  /** Allows the panel to be collapsed via its header (used on multi-card pages). */
  collapsible?: boolean;
}

const MeetingAgendaPanel: React.FC<MeetingAgendaPanelProps> = ({ meeting, isGenerating, onGenerate, canEdit = false, onSave, collapsible = false }) => {
  const [focus, setFocus] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [editItems, setEditItems] = useState<AgendaItem[] | null>(null);
  const [saving, setSaving] = useState(false);
  const items = meeting.agenda?.items || [];

  const isEditing = editItems !== null;

  const startEditing = () =>
    setEditItems(items.map((i) => ({ ...i, talking_points: i.talking_points ? [...i.talking_points] : [] })));
  const cancelEditing = () => setEditItems(null);

  const updateEditItem = (idx: number, patch: Partial<AgendaItem>) =>
    setEditItems((prev) => (prev ? prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)) : prev));
  const removeEditItem = (idx: number) =>
    setEditItems((prev) => (prev ? prev.filter((_, i) => i !== idx) : prev));
  const moveEditItem = (idx: number, delta: number) =>
    setEditItems((prev) => {
      if (!prev) return prev;
      const next = idx + delta;
      if (next < 0 || next >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[next]] = [copy[next], copy[idx]];
      return copy;
    });
  const addEditItem = () =>
    setEditItems((prev) => [...(prev || []), { title: '', description: '', duration_minutes: null, talking_points: [] }]);

  const saveEditing = async () => {
    if (!editItems || !onSave) return;
    const cleaned = editItems
      .map((it) => ({ ...it, title: it.title.trim() }))
      .filter((it) => it.title.length > 0);
    setSaving(true);
    try {
      await onSave(cleaned);
      setEditItems(null);
    } finally {
      setSaving(false);
    }
  };

  const toggleItem = (idx: number) =>
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  const totalMinutes = items.reduce((sum, i) => sum + (i.duration_minutes || 0), 0);
  const isCollapsed = collapsible && collapsed;
  const showGenerateButton = !(meeting.status === 'completed' && items.length > 0);

  return (
    <div className="meeting-panel">
      <div
        className={`meeting-panel-header${collapsible ? ' meeting-panel-header-collapsible' : ''}`}
        role={collapsible ? 'button' : undefined}
        aria-expanded={collapsible ? !collapsed : undefined}
        onClick={collapsible ? () => setCollapsed((c) => !c) : undefined}
      >
        <div>
          <h3 className="meeting-panel-title">Agenda</h3>
          {items.length > 0 && (
            <p className="meeting-panel-subtitle">
              {items.length} items · {totalMinutes} min planned
              {meeting.agenda?.user_modified ? ' · edited' : meeting.agenda?.generated_at ? ' · AI generated' : ''}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {canEdit && onSave && items.length > 0 && !isEditing && (
            <button
              className="meeting-btn meeting-btn-ghost meeting-btn-sm"
              onClick={(e) => { e.stopPropagation(); setCollapsed(false); startEditing(); }}
              disabled={isGenerating}
            >
              Edit
            </button>
          )}
          {showGenerateButton && !isEditing && (
            <button
              className="meeting-btn meeting-btn-ghost meeting-btn-sm"
              onClick={(e) => { e.stopPropagation(); onGenerate(focus.trim() || undefined); }}
              disabled={isGenerating}
            >
              {items.length > 0 ? 'Regenerate' : 'Generate Agenda'}
            </button>
          )}
          {collapsible && (
            <span className={`meeting-panel-chevron ${collapsed ? '' : 'expanded'}`}>▾</span>
          )}
        </div>
      </div>

      {isCollapsed ? null : isEditing ? (
        <div className="agenda-edit">
          {editItems!.map((item, idx) => (
            <div className="agenda-edit-item" key={item.item_id || idx}>
              <div className="agenda-edit-row">
                <input
                  className="meeting-form-input agenda-edit-title"
                  type="text"
                  placeholder="Agenda item title"
                  value={item.title}
                  onChange={(e) => updateEditItem(idx, { title: e.target.value })}
                />
                <input
                  className="meeting-form-input agenda-edit-duration"
                  type="number"
                  min={0}
                  placeholder="min"
                  value={item.duration_minutes ?? ''}
                  onChange={(e) =>
                    updateEditItem(idx, { duration_minutes: e.target.value === '' ? null : Number(e.target.value) })
                  }
                />
                <div className="agenda-edit-reorder">
                  <button
                    type="button"
                    className="outcomes-editor-iconbtn"
                    onClick={() => moveEditItem(idx, -1)}
                    disabled={idx === 0}
                    aria-label={`Move agenda item ${idx + 1} up`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="outcomes-editor-iconbtn"
                    onClick={() => moveEditItem(idx, 1)}
                    disabled={idx === editItems!.length - 1}
                    aria-label={`Move agenda item ${idx + 1} down`}
                  >
                    ↓
                  </button>
                </div>
                <button
                  className="meeting-btn meeting-btn-ghost meeting-btn-sm"
                  onClick={() => removeEditItem(idx)}
                  aria-label={`Remove agenda item ${idx + 1}`}
                >
                  Remove
                </button>
              </div>
              <textarea
                className="meeting-form-input agenda-edit-desc"
                placeholder="Description (optional)"
                value={item.description || ''}
                onChange={(e) => updateEditItem(idx, { description: e.target.value })}
              />
            </div>
          ))}
          <div className="agenda-edit-actions">
            <button className="meeting-btn meeting-btn-ghost meeting-btn-sm" onClick={addEditItem} disabled={saving}>
              + Add item
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="meeting-btn meeting-btn-ghost meeting-btn-sm" onClick={cancelEditing} disabled={saving}>
                Cancel
              </button>
              <button className="meeting-btn meeting-btn-primary meeting-btn-sm" onClick={saveEditing} disabled={saving}>
                {saving ? 'Saving…' : 'Save agenda'}
              </button>
            </div>
          </div>
        </div>
      ) : isGenerating ? (
        <div className="meeting-generating">
          <span className="spinner" /> Generating agenda from meeting materials...
        </div>
      ) : items.length === 0 ? (
        <>
          <div className="meeting-empty-hint">
            No agenda yet. Upload materials, then generate an agenda grounded in them — or add an
            optional focus below first.
          </div>
          <input
            className="meeting-form-input"
            style={{ marginTop: 10, width: '100%' }}
            type="text"
            placeholder="Optional focus, e.g. 'prioritize budget decisions'"
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
          />
        </>
      ) : (
        <div className="agenda-list">
          {items.map((item, idx) => {
            const hasDetail = Boolean(item.description || (item.talking_points && item.talking_points.length > 0));
            const isExpanded = expandedItems.has(idx);
            return (
              <div
                className={`agenda-item${hasDetail ? ' agenda-item-expandable' : ''}`}
                key={item.item_id || idx}
                role={hasDetail ? 'button' : undefined}
                aria-expanded={hasDetail ? isExpanded : undefined}
                onClick={hasDetail ? () => toggleItem(idx) : undefined}
              >
                <div className="agenda-item-index">{idx + 1}</div>
                <div className="agenda-item-content">
                  <div className="agenda-item-title-row">
                    <h4 className="agenda-item-title">{item.title}</h4>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {item.duration_minutes ? (
                        <span className="agenda-item-duration">{item.duration_minutes} min</span>
                      ) : null}
                      {hasDetail && (
                        <span className={`meeting-panel-chevron ${isExpanded ? 'expanded' : ''}`}>▾</span>
                      )}
                    </span>
                  </div>
                  {isExpanded && item.description && <p className="agenda-item-desc">{item.description}</p>}
                  {isExpanded && item.talking_points && item.talking_points.length > 0 && (
                    <ul className="agenda-item-points">
                      {item.talking_points.map((point, pIdx) => (
                        <li key={pIdx}>{point}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MeetingAgendaPanel;
